import { useState, useCallback, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Loader2, MessageSquare, Bell, ChevronDown, ChevronUp, CalendarClock } from 'lucide-react';
import { discussionsApi, projectsApi } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { RichTextEditor, EVERYONE_MENTION_ID } from '@/components/ui/RichTextEditor';
import { useFileAttachments } from '@/hooks/useFileAttachments';
import { useAuthStore } from '@/stores/auth.store';
import { useProjectRoles } from '@/hooks/useProjectRoles';
import { useMentionableUsers } from '@/hooks/useMentionableUsers';
import { useDraft } from '@/hooks/useDraft';
import { cn } from '@/lib/utils';

interface CreateDiscussionModalProps {
  isOpen: boolean;
  projectId: string;
  onClose: () => void;
  onSuccess?: (discussion: any) => void;
  discussionType?: string;
  typeLabel?: string;
}

interface ProjectMember {
  userId: string;
  role: string;
  canReceiveDiscussionEmails?: boolean;
  user: {
    id: string;
    name: string;
    email: string;
    avatarUrl?: string;
  };
}

export function CreateDiscussionModal({ isOpen, projectId, onClose, onSuccess, discussionType, typeLabel }: CreateDiscussionModalProps) {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const titleDraft = useDraft(`draft:new-discussion:${projectId ?? ''}:title`);
  const contentDraft = useDraft(`draft:new-discussion:${projectId ?? ''}:content`);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [mentionedUserIds, setMentionedUserIds] = useState<string[]>([]);
  const [notifyUserIds, setNotifyUserIds] = useState<string[]>([]);
  const [showNotifySection, setShowNotifySection] = useState(false);
  const [notifyInitialized, setNotifyInitialized] = useState(false);
  // Scheduled posting (agency members only)
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduledFor, setScheduledFor] = useState('');
  // Staff on THIS project: Solid anywhere, a partner agency on its own
  const { isProjectStaff: canSchedule } = useProjectRoles(projectId);

  // Get mentionable users for @mentions (broader list including org members)
  const { data: mentionableUsers = [] } = useMentionableUsers(projectId);

  // Get project data to access project members for notification list
  const { data: projectData } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId),
    enabled: isOpen && !!projectId,
  });

  // Get project members from the fetched project data (for notification recipients)
  const projectMembers: ProjectMember[] = projectData?.members || [];

  // Get notification defaults for this project
  const { data: defaultNotifyIds = [] } = useQuery({
    queryKey: ['project-notification-defaults', projectId],
    queryFn: () => projectsApi.getNotificationDefaults(projectId),
    enabled: isOpen && !!projectId,
  });

  // Initialize notify user IDs from defaults when they load
  // Filter out the current user and users who have notifications disabled
  useEffect(() => {
    if (isOpen && defaultNotifyIds.length > 0 && !notifyInitialized && projectMembers.length > 0) {
      const eligibleDefaults = defaultNotifyIds.filter((userId) => {
        // Don't notify yourself
        if (userId === user?.id) return false;
        const member = projectMembers.find((m) => m.userId === userId);
        return member?.canReceiveDiscussionEmails !== false;
      });
      setNotifyUserIds(eligibleDefaults);
      setNotifyInitialized(true);
    }
  }, [isOpen, defaultNotifyIds, notifyInitialized, projectMembers, user?.id]);

  // Restore drafts when modal opens
  useEffect(() => {
    if (isOpen) {
      setTitle(titleDraft.read());
      setContent(contentDraft.read());
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get the first organization ID (or use a default)
  const organizationId = user?.organizations?.[0]?.id || '';

  // Plain attachments upload after the discussion exists; embedded images upload as drafts on paste.
  const attachments = useFileAttachments({ organizationId, entityType: 'message', projectId });

  const handleMention = useCallback((userId: string) => {
    if (userId === EVERYONE_MENTION_ID) {
      // @everyone: select every member that can be notified
      const everyoneIds = projectMembers
        .filter((m) => m.userId !== user?.id && m.canReceiveDiscussionEmails !== false)
        .map((m) => m.userId);
      setNotifyUserIds(prev => [...new Set([...prev, ...everyoneIds])]);
      setShowNotifySection(true);
      return;
    }
    setMentionedUserIds(prev => prev.includes(userId) ? prev : [...prev, userId]);
  }, [projectMembers, user?.id]);

  const toggleNotifyUser = useCallback((userId: string) => {
    setNotifyUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  }, []);

  const resetForm = () => {
    setTitle('');
    setContent('');
    attachments.clearFiles();
    setMentionedUserIds([]);
    setNotifyUserIds([]);
    setShowNotifySection(false);
    setNotifyInitialized(false);
    setScheduleEnabled(false);
    setScheduledFor('');
  };
  
  const createMutation = useMutation({
    mutationFn: async (data: { title: string; content: string; notifyUserIds?: string[]; discussionType?: string; scheduledFor?: string; attachmentIds?: string[] }) => {
      // Create the discussion first
      const discussion = await discussionsApi.create(projectId, data);

      // Then upload any staged attachments (embedded images are already up and were linked by attachmentIds)
      if (attachments.hasFiles) {
        await attachments.uploadAll(discussion.id);
      }

      return discussion;
    },
    onSuccess: (discussion) => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['discussions', projectId] });
      queryClient.invalidateQueries({ queryKey: ['scheduled-discussions', projectId] });
      titleDraft.clear();
      contentDraft.clear();
      resetForm();
      onClose();
      onSuccess?.(discussion);
    },
  });

  const scheduling = scheduleEnabled && !!scheduledFor;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;
    if (scheduleEnabled && !scheduledFor) return;

    createMutation.mutate({
      title: title.trim(),
      content: content.trim(),
      attachmentIds: attachments.collectAttachmentIds(content),
      notifyUserIds: notifyUserIds.length > 0 ? notifyUserIds : undefined,
      ...(discussionType && { discussionType }),
      ...(scheduling && { scheduledFor: new Date(scheduledFor).toISOString() }),
    });
  };
  
  const handleClose = () => {
    if (!createMutation.isPending) {
      titleDraft.clear();
      contentDraft.clear();
      resetForm();
      onClose();
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-secondary/60 backdrop-blur-sm animate-fade-in"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-elevated animate-scale-in overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-grey-100">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-accent/10">
              <MessageSquare className="w-5 h-5 text-accent" />
            </div>
            <h2 className="text-heading-4 text-secondary">{typeLabel ? `Start a new ${typeLabel.toLowerCase()}` : 'Start a new discussion'}</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={createMutation.isPending}
            className="p-2 hover:bg-grey-100 rounded-full transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-grey-500" />
          </button>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="px-6 py-6 space-y-5 overflow-y-auto flex-1">
            {/* Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-secondary mb-2">
                Title <span className="text-error">*</span>
              </label>
              <input
                id="title"
                type="text"
                value={title}
                onChange={(e) => { setTitle(e.target.value); titleDraft.write(e.target.value); }}
                placeholder="Type a title for this discussion..."
                className="input text-lg font-semibold"
                required
                autoFocus
                disabled={createMutation.isPending}
              />
            </div>
            
            {/* Content - Rich Text Editor */}
            <div>
              <label className="block text-sm font-medium text-secondary mb-2">
                Discussion Content <span className="text-error">*</span>
              </label>
              <RichTextEditor
                content={content}
                onChange={(html) => { setContent(html); contentDraft.write(html); }}
                placeholder="Write your discussion content here... Type @ to mention someone"
                minHeight="180px"
                enableAttachments={true}
                stagedFiles={attachments.stagedFiles}
                onFilesAdded={attachments.addFiles}
                onFileRemoved={attachments.removeFile}
                onEmbedImage={attachments.embedImage}
                onDemoteEmbed={attachments.addUploaded}
                disabled={createMutation.isPending}
                enableMentions={true}
                mentionableUsers={mentionableUsers}
                onMention={handleMention}
                enableEveryoneMention={true}
              />
            </div>

            {/* Notification Recipients */}
            <div className="border border-grey-200 rounded-xl overflow-hidden">
              <button
                type="button"
                onClick={() => setShowNotifySection(!showNotifySection)}
                className="w-full flex items-center justify-between px-4 py-3 bg-grey-50 hover:bg-grey-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Bell className="w-4 h-4 text-grey-500" />
                  <span className="text-sm font-medium text-secondary">
                    Notify {notifyUserIds.length > 0 ? `(${notifyUserIds.length} ${notifyUserIds.length === 1 ? 'person' : 'people'})` : ''}
                  </span>
                </div>
                {showNotifySection ? (
                  <ChevronUp className="w-4 h-4 text-grey-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-grey-400" />
                )}
              </button>

              {showNotifySection && (
                <div className="p-3 border-t border-grey-200 max-h-48 overflow-y-auto">
                  {projectMembers.filter((m) => m.userId !== user?.id).length === 0 ? (
                    <p className="text-sm text-grey-500 text-center py-2">
                      No members available to notify
                    </p>
                  ) : (
                    <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                      {projectMembers.filter((member) => member.userId !== user?.id).map((member) => {
                        const isSelected = notifyUserIds.includes(member.userId);
                        const canReceive = member.canReceiveDiscussionEmails !== false;
                        const isClient = (member as any).isClient === true;
                        return (
                          <label
                            key={member.userId}
                            className={cn(
                              "flex items-center gap-1.5 py-1 rounded transition-colors",
                              canReceive ? "cursor-pointer" : "cursor-not-allowed opacity-50",
                              canReceive && (isSelected ? "bg-accent/10" : "hover:bg-grey-50")
                            )}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected && canReceive}
                              onChange={() => canReceive && toggleNotifyUser(member.userId)}
                              disabled={!canReceive}
                              className="w-3.5 h-3.5 rounded border-grey-300 text-accent focus:ring-accent disabled:opacity-50"
                            />
                            <span className={cn("text-sm truncate", canReceive ? "text-secondary" : "text-grey-400")}>
                              {member.user.name}
                            </span>
                            {isClient && canReceive && (
                              <span className="text-[10px] text-grey-400">(client)</span>
                            )}
                            {!canReceive && (
                              <span className="text-[10px] text-grey-400">(off)</span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Schedule for later (agency members only) */}
            {canSchedule && (
              <div className="border border-grey-200 rounded-xl overflow-hidden">
                <label className="w-full flex items-center justify-between px-4 py-3 bg-grey-50 hover:bg-grey-100 transition-colors cursor-pointer">
                  <div className="flex items-center gap-2">
                    <CalendarClock className="w-4 h-4 text-grey-500" />
                    <span className="text-sm font-medium text-secondary">Schedule for later</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={scheduleEnabled}
                    onChange={(e) => setScheduleEnabled(e.target.checked)}
                    disabled={createMutation.isPending}
                    className="w-4 h-4 rounded border-grey-300 text-accent focus:ring-accent"
                  />
                </label>
                {scheduleEnabled && (
                  <div className="p-4 border-t border-grey-200 space-y-2">
                    <label htmlFor="scheduled-for" className="block text-sm font-medium text-secondary">
                      Post at
                    </label>
                    <input
                      id="scheduled-for"
                      type="datetime-local"
                      value={scheduledFor}
                      min={new Date(Date.now() + 60_000).toISOString().slice(0, 16)}
                      onChange={(e) => setScheduledFor(e.target.value)}
                      className="input"
                      disabled={createMutation.isPending}
                    />
                    <p className="text-xs text-grey-500">
                      Until then, only you and agency admins can see it. Notifications go out when it posts.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 bg-grey-50 border-t border-grey-100">
            <Button 
              type="button" 
              variant="ghost" 
              onClick={handleClose}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!title.trim() || !content.trim() || (scheduleEnabled && !scheduledFor) || createMutation.isPending || attachments.isUploading}
            >
              {createMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {scheduling ? 'Scheduling...' : 'Posting...'}
                </>
              ) : scheduling ? (
                'Schedule'
              ) : (
                'Post Discussion'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
